import { useMemo, useState } from 'react';
import { useMutation } from '@apollo/client';
import get from 'lodash/get';
import { AnalysisStatus, QueryType } from '../../apollo/graphqlSchema';
import { useDispatch } from 'react-redux';
import {
  getExecutionResponseByType,
  getMutationByQueryType,
  validateFormula,
} from './utils';
import { setErrorAction } from '../../../redux/slices/oldBuilder';
import printGraphqlErrors from '../../../utils/apollo/printGraphqlErrors';
import { useParams } from 'react-router-dom';
import { useAppSelector } from '../../../redux/hooks';
import { builderSelector } from '../../../redux/selectors/builder';
import { generateExecutionInput } from '../../../components/Builder/utils/validateExecutionInput';
import { assertCondition, assertNotNil } from '../../types';
import toastMessage from '../../toastMessage';
import { useAnalytics } from '../../analytics/useAnalytics';
import { reportCreated } from '../../analytics/events/report/events';
import { MaybeNil } from '../../interfaces';
import { formErrorViewed } from '../../analytics/events/error/events';
import { countBuilderErrors } from '../../../components/Builder/utils/errorUtil';
import { AnalyticsErrorContext } from '../../analytics/events/error/types';
import useCachedEntitiesSelector from '../../../redux/selectorHooks/useCachedEntitiesSelector';
import { IAnalysisResponse } from '../../apollo/responseTypes';
import useExecutedReport from '../../builder/useExecutedReport';
import { analysisSelector } from '../../../redux/selectors/analyses';

type FormulaExecute = [() => Promise<unknown>, boolean, boolean];

export default (queryType: QueryType): FormulaExecute => {
  const dispatch = useDispatch();
  const [isMutationLoading, setIsMutationLoading] = useState(false);
  const builderState = useAppSelector(builderSelector);
  const replaceReport = useCachedEntitiesSelector('replaceReport');
  const { track } = useAnalytics();

  const urlParams = useParams();
  const builderId = urlParams?.builderId;

  const handleExecutedReport = useExecutedReport();

  const analysisMemoizedSelector = useMemo(analysisSelector, []);
  const analysis = useAppSelector((state) =>
    analysisMemoizedSelector(state, builderId),
  );

  assertCondition(
    queryType !== QueryType.Significance,
    'Significance has its own component',
  );

  const mutation = getMutationByQueryType[queryType];
  const [execute] = useMutation(mutation);

  const executeFormula = async () => {
    const executionInput = generateExecutionInput({
      queryType,
      state: builderState,
      replaceReport,
      analysisName: analysis?.metadata?.display,
      analysisDescription: analysis?.metadata?.description,
    });

    // if not validate input, highlight the errors and return.
    if (!executionInput.isStateValid) {
      const { errors } = executionInput;
      dispatch(setErrorAction(errors));
      track(
        formErrorViewed({
          message: `Failed to execute report. ${countBuilderErrors(errors)} errors found.`,
          context: AnalyticsErrorContext.ExecuteReport,
        }),
      );
      return;
    }

    const { input } = executionInput;
    setIsMutationLoading(true);
    // Execute the query mutation
    const response = await execute({
      variables: { input },
    });

    const { entity: untypedEntity, errors } = get(
      response,
      getExecutionResponseByType[queryType],
    );

    if (printGraphqlErrors(errors)) {
      setIsMutationLoading(false);
      return;
    }

    const entity = untypedEntity as MaybeNil<IAnalysisResponse>;
    assertNotNil(entity);

    if (entity.status === AnalysisStatus.Failed) {
      toastMessage({
        message: 'Execution Failed.',
        type: 'error',
      });
    }

    handleExecutedReport(entity);
    track(reportCreated(entity));
    setIsMutationLoading(false);
  };
  const isValidFormula = validateFormula(queryType, builderState);
  return [executeFormula, isValidFormula, isMutationLoading];
};
